import React, { useState, useEffect, useRef } from "react";
import Pagelayout from "../../components/Pagelayout";
import CustomTable from "../../components/ListPageComponent";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { 
  Box, 
  TextField, 
  MenuItem, 
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import { getEOSSList } from "../../parollAPIs";
import dayjs from 'dayjs';

const EossPayroll = () => {
  const navigate = useNavigate();
  
  // Get currency from company profile (Settings page)
  const companyProfileSlice = useSelector((state) => state.companyProfileSlice);
  const companyProfileData = companyProfileSlice?.userValue || companyProfileSlice?.value;
  
  // Get currency symbol - robust resolution (same pattern used across app)
  const resolveCurrencySymbol = () => {
    const profile = companyProfileData?.profile || companyProfileData || {};
    const cur = profile?.currency;

    // 1) Explicit symbol configured
    if (profile?.currencySymbol && String(profile.currencySymbol).trim()) {
      return profile.currencySymbol;
    }

    // 2) Common object shapes
    if (cur && typeof cur === 'object') {
      if (cur.symbol) return cur.symbol;
      if (cur.cc) return cur.cc;
      if (cur.name) return cur.name;
      if (cur.code) return cur.code;
    }

    // 3) String types: avoid ObjectId
    if (typeof cur === 'string' && cur.trim()) {
      const s = cur.trim();
      const isObjectId = /^[a-fA-F0-9]{24}$/.test(s);
      if (!isObjectId) {
        // Map common currency codes to symbols
        const map = { INR: '₹', AED: 'AED ', USD: '$', EUR: '€', SAR: '﷼', QAR: 'QAR ', OMR: 'OMR ', KWD: 'KWD ' };
        return map[s.toUpperCase()] || s;
      }
    }

    // 4) Fallback sensible default
    return '₹';
  };
  const currency = resolveCurrencySymbol();
  
  // State management
  const [eossList, setEossList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Print functionality using direct window.print
  const handlePrint = () => {
    console.log('🖨️ Opening print window for EOSS List');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>EOSS List - ${dayjs().format('DD MMM YYYY')}</title>
        <style>
          @page { size: A4 landscape; margin: 15mm; }
          body { font-family: Arial, sans-serif; font-size: 12px; }
          h1 { text-align: center; font-size: 18px; margin-bottom: 20px; }
          .meta { text-align: center; margin-bottom: 30px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; font-weight: bold; }
          .status-draft { background-color: #fff3cd; color: #856404; }
          .status-posted { background-color: #d4edda; color: #155724; }
          .footer { text-align: center; margin-top: 30px; font-size: 11px; color: #666; }
        </style>
      </head>
      <body>
        <h1>End of Service Settlement (EOSS) Report</h1>
        <div class="meta">
          <p><strong>Generated on:</strong> ${dayjs().format('DD MMM YYYY, hh:mm A')}</p>
          <p><strong>Status:</strong> ${filters.status || 'All'} | <strong>Search:</strong> ${filters.search || 'None'}</p>
          <p><strong>Total Records:</strong> ${formattedData.length}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>EOSS Number</th>
              <th>Date</th>
              <th>Employee Name</th>
              <th>Employee Code</th>
              <th>Type</th>
              <th>Service</th>
              <th>Settlement Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${formattedData.map(item => `
              <tr>
                <td>${item.sequence}</td>
                <td>${dayjs(item.date).format('DD MMM YYYY')}</td>
                <td>${item.employeeName}</td>
                <td>${item.employeeCode}</td>
                <td>${item.type}</td>
                <td>${item.serviceYears}</td>
                <td align="right">${item.settlementAmount}</td>
                <td class="status-${item.statusDisplay.toLowerCase()}">${item.statusDisplay}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">
          <p>This is a system-generated document. No signature required.</p>
          <p>Page 1 of 1</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  });
  const [filters, setFilters] = useState({
    search: "",
    status: ""
  });

  // Table headers with modern styling
  const headers = [
    { key: "sequence", label: "EOSS Number" },
    { key: "date", label: "Date" },
    { key: "employeeName", label: "Employee Name" },
    { key: "employeeCode", label: "Employee Code" },
    { key: "type", label: "Type" },
    { key: "serviceYears", label: "Service (Years)" },
    { key: "settlementAmount", label: "Settlement Amount" },
    { key: "status", label: "Status" },
  ];

  const visibleColumns = [
    "sequence",
    "date",
    "employeeName",
    "employeeCode",
    "type",
    "serviceYears",
    "settlementAmount",
    "status",
  ];

  // Fetch EOSS list
  const fetchEOSSData = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      await getEOSSList({
        page,
        limit: pagination.limit,
        search: filters.search,
        status: filters.status,
        successCallback: (response) => {
          setEossList(response.data || []);
          setPagination(response.pagination || pagination);
          setLoading(false);
        },
        errorCallback: (err) => {
          setError(err.message || "Failed to load EOSS records");
          setLoading(false);
        }
      });
    } catch (err) {
      setError("An unexpected error occurred");
      setLoading(false);
    }
  };

  // Load data on mount and filter changes
  useEffect(() => {
    fetchEOSSData(1);
  }, []); // Empty dependency array means load only on mount
  
  // Reload when filters change
  useEffect(() => {
    fetchEOSSData(1);
  }, [filters.status]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search !== undefined) {
        fetchEOSSData(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.search]);

  // Handle row click - navigate to edit
  const handleRowClick = (rowData) => {
    navigate(`/userdashboard/payroll/attendance/EossPayroll/${rowData._id}`);
  };

  // Handle create button
  const handleCreateBtn = () => {
    navigate("create");
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    fetchEOSSData(newPage);
  };

  // Status color mapping
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'draft':
        return 'warning';
      case 'posted':
      case 'done':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // Format data for display
  const formattedData = eossList.map(item => ({
    ...item,
    serviceYears: `${item.serviceYears || 0}y ${item.serviceMonths || 0}m`,
    settlementAmount: `${currency}${Number(item.settlementAmount || 0).toFixed(2)}`,
    // Keep status as string for navigation, render Chip in customCellRender
    statusDisplay: item.status || 'DRAFT'
  }));

  // Custom cell renderer for status column
  const customCellRender = (column, row) => {
    if (column === 'status') {
      return (
        <Chip 
          label={row.statusDisplay} 
          color={getStatusColor(row.statusDisplay)}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      );
    }
    return null;
  };

  return (
    <Pagelayout
      pageName="End of Service Settlement (EOSS)"
      headerLayout={1}
      createBtn={true}
      handleCreateBtn={handleCreateBtn}
    >
      {/* Filters Section */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search by EOSS Number"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          sx={{ minWidth: 250 }}
          placeholder="e.g., EOS/2025/11/0001"
        />
        
        <TextField
          select
          size="small"
          label="Status"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Status</MenuItem>
          <MenuItem value="Draft">Draft</MenuItem>
          <MenuItem value="Posted">Posted</MenuItem>
          <MenuItem value="Cancelled">Cancelled</MenuItem>
        </TextField>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="no-print">
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          EOSS Records ({pagination.totalRecords || 0})
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={handlePrint}
          disabled={loading || formattedData.length === 0}
          startIcon={<span>🖨️</span>}
        >
          Print List
        </Button>
      </Box>

      {/* Table */}
      <CustomTable
        headers={headers}
        data={formattedData}
        visibleColumns={visibleColumns}
        onRowClick={handleRowClick}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        customCellRender={customCellRender}
      />
      

      {/* Empty State */}
      {!loading && formattedData.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No EOSS Records Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filters.search || filters.status 
              ? "Try adjusting your filters" 
              : "Click 'Create' to add your first EOSS record"}
          </Typography>
        </Box>
      )}
    </Pagelayout>
  );
};

export default EossPayroll;
